import { isProxy, toRaw, isRef, unref } from "vue";

function deproxy(value, seen = new WeakMap()) {
	// Unwrap Vue refs
	if (isRef?.(value)) {
		return deproxy(unref(value), seen);
	}

	// Strip Vue proxy wrapper
	if (isProxy?.(value)) {
		value = toRaw(value);
	}

	// Primitives/null
	if (value === null || typeof value !== "object") return value;

	// Cycle guard
	if (seen.has(value)) return seen.get(value);

	// Preserve common built-ins without losing type
	if (value instanceof Date) return new Date(value.getTime());
	if (value instanceof RegExp) return new RegExp(value.source, value.flags);
	if (value instanceof Map) {
		const m = new Map();
		seen.set(value, m);
		for (const [k, v] of value.entries()) m.set(deproxy(k, seen), deproxy(v, seen));
		return m;
	}
	if (value instanceof Set) {
		const s = new Set();
		seen.set(value, s);
		for (const v of value.values()) s.add(deproxy(v, seen));
		return s;
	}
	if (ArrayBuffer.isView(value)) {
		// TypedArrays/DataView: clone buffer
		return new value.constructor(value);
	}
	if (value instanceof ArrayBuffer) {
		return value.slice(0);
	}

	// Plain Array / Object
	const out = Array.isArray(value) ? [] : {};
	seen.set(value, out);
	// Only enumerate own, enumerable props
	for (const key of Object.keys(value)) {
		const v = value[key];
		// Skip functions & DOM nodes (not cloneable and usually not desired)
		if (typeof v === "function") continue;
		if (typeof Node !== "undefined" && v instanceof Node) continue;
		out[key] = deproxy(v, seen);
	}
	return out;
}

export function clone(data = {}) {
	const plain = deproxy(data);
	if (typeof structuredClone === "function") {
		return structuredClone(plain);
	}
	// If you only need to clone plain objects/arrays without deep nesting:
	// return Array.isArray(data) ? [...data] : { ...data };
	return JSON.parse(JSON.stringify(plain)); // safe deep clone for legacy
}

export function formatData(result, key = "id") {
    if (!result || !result.data || result.data.length === 0) {
        return {};
    }

    // Store result
    const object = clone(result);
    // But group the result.data under their id's
    object["data"] = {};
    for (let counter = 0; counter < result.data.length; counter++) {
        const data = result.data[counter].data;
        const extra = result.data[counter].extra;
        if (data) {
            object["data"][data[key]] = {
                extra: extra,
                ...data
            };
        }
    }
    return object;
}

// Returns object, based on FormData or object
export function resolveObject(data) {
	let result = data;
	if (typeof data === "string") {
		let tmpObject;
		try {
			tmpObject = JSON.parse(data);
		} catch (e) { }
		if (tmpObject) {
			result = tmpObject;
		}
	}
    if (data instanceof FormData) {
        result = {};
        data.forEach((value, key) => result[key] = value);
    }
    return result;
}

// Returns only data based on API result
export function resolveData(result) {
    return result.data.map(a => {
        const data = a.data;
        data.extra = a.extra;
        data._meta = a._meta;
		data.images = a.images;
        return data;
    });
}


// Resolve url function
export const resolveUrl = (item) => {
    let url = `/${item.link_module_object.toLowerCase()}/${item.link_item_id}`;
    if (item.link_module_object === "URL") {
        url = item.link_custom_url;
    }
    return url;
};
// Reorder the array based on old and new index
export function reorderArray(arr, old_index, new_index) {
	new_index = new_index >= 0 ? new_index : 0;
	if (new_index >= arr.length) {
		var k = new_index - arr.length + 1;
		while (k--) {
			arr.push(undefined);
		}
	}
	arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
	return arr;
};